import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import styled, { css } from "styled-components";

import Icon from "components/atoms/Icon";
import Tooltip from "components/atoms/Tooltip";
import useToast from "components/atoms/toast/useToast";

import ContextMenu from "components/molecules/ContextMenu";
import AbstractModal from "components/page/modal/AbstractModal";
import PaymentSubscribeModal from "components/page/modal/PaymentSubscribeModal";

import { usePatchSpace, useSpace, useUser } from "hooks/queries/useUser";

import Constants from "utils/constants";

const SpaceBarConatiner = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    width: ${Constants.SPACE_BAR_WIDTH}px;
    height: calc(100vh - ${Constants.MOBILE_HEADER_HEIGHT}px);
    background-color: var(--color-White);
    border-right: 1px solid var(--color-Grey3);
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 12px 0;
    z-index: 7;
`;

const SpaceDivider = styled.div`
    width: 28px;
    height: 1px;
    background: var(--color-Grey3);
    margin: 6px 0 6px 0;
`;

const SpaceItem = styled.div`
    margin: 4px 0;
`;

const SpaceIconWrap = styled.div`
    position: relative;
    display: inline-flex;
`;

const SpaceCircleButton = styled.button`
    width: ${Constants.SPACE_ICON_SIZE}px;
    height: ${Constants.SPACE_ICON_SIZE}px;
    border-radius: 50%;
    border: 0;
    outline: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: ${(p) => (p.readonly ? "not-allowed" : "pointer")};
    box-shadow: ${(p) =>
        p.active ? "0 0 0 2px var(--color-Key)" : p.image ? "0 0 0 1px var(--color-SubBlack)" : "none"};
    background-color: ${(p) => (p.active ? p.bg || "var(--color-Primary)" : "white")};
    transition: transform 0.15s ease, box-shadow 0.15s ease, filter 0.15s ease;
    &:hover {
        background-color: ${(p) => (p.readonly ? "transparent" : "var(--color-ButtonHover4)")};
    }
    &:focus-visible {
        box-shadow: 0 0 0 2px var(--color-Key), 0 0 0 4px rgba(0, 0, 0, 0.06);
    }
`;

const SpaceAvatarImg = styled.img`
    width: 100%;
    height: 100%;
    border-radius: 50%;
    object-fit: cover;
    display: block;

    filter: ${(p) => {
        if (p.status === "INACTIVE") return "grayscale(70%) contrast(0.9)";
        return "none";
    }};
    pointer-events: none;
    user-select: none;
`;

const SpaceAvatarFallback = styled.div`
    width: 100%;
    height: 100%;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;

    font-size: 1.4rem;
    font-weight: 600;
    text-transform: uppercase;

    background-color: var(--color-ButtonHover4);
    color: black;

    filter: ${(p) => (p.status === "INACTIVE" ? "grayscale(70%) contrast(0.9)" : "none")};
    user-select: none;
    pointer-events: none;
`;

const HiddenItemRow = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    cursor: default;
`;

const HiddenItemName = styled.span`
    flex: 1;
    font-size: 1.3rem;
    color: var(--color-Black);
`;

const HiddenItemUnhide = styled.button`
    border: 0;
    background: transparent;
    padding: 0;
    margin: 0;
    font-size: 1.3rem;
    color: var(--color-Key);
    text-decoration: underline;
    cursor: pointer;

    &:hover {
        opacity: 0.85;
    }
`;

const HiddenItemAvatar = styled.div`
    width: 40px;
    height: 40px;
    border-radius: 50%;
    overflow: hidden;
    box-shadow: ${(p) => (p.image ? "0 0 0 1px var(--color-SubBlack)" : "none")};
    flex-shrink: 0;
`;

const HiddenItemImg = styled.img`
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;

    filter: ${(p) => (p.status === "INACTIVE" ? "grayscale(70%) contrast(0.9)" : "none")};
`;

const HiddenItemFallback = styled.div`
    width: 100%;
    height: 100%;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;

    font-size: 1.3rem;
    font-weight: 600;
    text-transform: uppercase;

    background-color: var(--color-ButtonHover4);
    color: black;

    filter: ${(p) => (p.status === "INACTIVE" ? "grayscale(70%) contrast(0.9)" : "none")};
`;

const SpaceBadge = styled.span`
    position: absolute;
    top: -4px;
    right: -4px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    box-sizing: border-box;
    border-radius: 999px;

    /* variant에 따라 사이즈/스타일 */
    ${(p) =>
        p.variant === "count"
            ? css`
                  min-width: 18px;
                  height: 18px;
                  padding: 0 6px;
                  font-size: 11px;
                  line-height: 1;
                  font-weight: 700;
              `
            : p.variant === "icon"
            ? css`
                  width: 18px;
                  height: 18px;
                  font-size: 12px;
              `
            : css`
                  /* dot */
                  width: 8px;
                  height: 8px;
              `}

    background: ${(p) => p.bg || "var(--color-Key)"};
    color: ${(p) => p.color || "#fff"};
`;

function SpaceBar() {
    const navigate = useNavigate();
    const location = useLocation();
    const getCurrentSpaceId = (pathname) => {
        const first = pathname.split("/")[1] || null;
        return first;
    };
    const { setToast } = useToast();
    const currentSpaceId = getCurrentSpaceId(location.pathname);
    const userQuery = useUser();

    const [showContextMenu, setShowContextMenu] = useState({
        index: null,
        x: 0,
        y: 0,
    });

    const [showHiddenGroup, setShowHiddenGroup] = useState({
        show: false,
        x: 0,
        y: 0,
    });

    const spaceQuery = useSpace();
    const [spaces, setSpaces] = useState([]);
    const updateSpaceMutation = usePatchSpace();

    useEffect(() => {
        if (spaceQuery.data) {
            setSpaces(spaceQuery.data);
        }
    }, [spaceQuery.data]);

    const [showSubscribeModal, setShowSubscribeModal] = useState();
    return (
        <div>
            <SpaceBarConatiner>
                {/* 나머지 그룹들 */}
                {spaces
                    .filter((space) => !space.is_hidden)
                    .map((space) => (
                        <SpaceItem className={"space-item-" + space.id} key={space.id}>
                            <Tooltip message={`${space.name}`} key={space.uuid}>
                                <SpaceIconWrap>
                                    <SpaceCircleButton
                                        active={currentSpaceId === String(space.uuid)}
                                        onClick={() => {
                                            if (space.status !== "PENDING") {
                                                navigate(`/${space.uuid}/reference`);
                                            } else {
                                                navigate(`/${space.uuid}/management`);
                                            }
                                        }}
                                        onContextMenu={(e) => {
                                            setShowContextMenu(
                                                showContextMenu.index === space.id
                                                    ? { index: null, x: 0, y: 0 }
                                                    : {
                                                          index: space.id,
                                                          x: e.currentTarget.getBoundingClientRect().left,
                                                          y: e.currentTarget.getBoundingClientRect().bottom,
                                                      },
                                            );
                                            e.preventDefault();
                                        }}
                                        image={!!space.image}
                                    >
                                        {space.image ? (
                                            <SpaceAvatarImg
                                                src={
                                                    process.env.REACT_APP_ENV === "local"
                                                        ? `http://${process.env.REACT_APP_API_BASE_URL}${space.image}`
                                                        : space.image
                                                }
                                                alt={space.name}
                                                status={space.status}
                                            />
                                        ) : (
                                            <SpaceAvatarFallback status={space.status}>
                                                {space.name?.trim()?.[0] || "?"}
                                            </SpaceAvatarFallback>
                                        )}
                                    </SpaceCircleButton>
                                    {space.status === "PENDING" && (
                                        <SpaceBadge variant="icon" bg={"var(--color-Key)"}>
                                            <Icon name={"info"} size={"12"} color={"var(--color-White)"} />
                                        </SpaceBadge>
                                    )}
                                </SpaceIconWrap>
                            </Tooltip>
                            {showContextMenu.index !== null && (
                                <ContextMenu
                                    show={true}
                                    position={{ x: showContextMenu.x, y: showContextMenu.y - 20 }}
                                    minWidth={"70px"}
                                    items={[
                                        {
                                            name: "숨기기",
                                            action: (e) => {
                                                updateSpaceMutation.mutate(
                                                    {
                                                        space_id: showContextMenu.index,
                                                        is_hidden: true,
                                                    },
                                                    {
                                                        onSuccess: (data, variables, context) => {
                                                            setToast("그룹이 숨겨졌습니다.", "info");
                                                            setShowContextMenu({
                                                                index: null,
                                                                x: 0,
                                                                y: 0,
                                                            });
                                                        },
                                                        onError: (error) => {
                                                            setToast(
                                                                "그룹 숨기기에 실패했습니다. 잠시후 시도해주세요.",
                                                                "error",
                                                            );
                                                        },
                                                    },
                                                );
                                            },
                                        },
                                    ]}
                                    onClose={() =>
                                        setShowContextMenu({
                                            index: null,
                                            x: 0,
                                            y: 0,
                                        })
                                    }
                                    triggerClassName={`space-item-${showContextMenu.index}`}
                                />
                            )}
                        </SpaceItem>
                    ))}
                <SpaceItem>
                    <Tooltip message={"그룹 추가"}>
                        <SpaceCircleButton
                            onClick={() => {
                                setShowSubscribeModal(true);
                            }}
                            image={true}
                        >
                            <Icon name={"plus"} size={"18"} color={"var(--color-SubBlack)"} />
                        </SpaceCircleButton>
                    </Tooltip>
                </SpaceItem>

                <SpaceDivider />

                <SpaceItem>
                    <Tooltip message={`숨겨진 그룹(${spaces.filter((space) => space.is_hidden).length || 0})`}>
                        <SpaceCircleButton
                            onClick={(e) => {
                                setShowHiddenGroup((prevState) => ({
                                    show: !prevState.show,
                                    x: e.currentTarget?.getBoundingClientRect().left,
                                    y: e.currentTarget?.getBoundingClientRect().bottom - 20,
                                }));
                                e.preventDefault();
                            }}
                            disabled={spaces.filter((space) => space.is_hidden).length === 0}
                            image={true}
                        >
                            <Icon name={"delete"} size={"18"} color={"var(--color-SubBlack)"} />
                        </SpaceCircleButton>

                        {showHiddenGroup.show && (
                            <ContextMenu
                                title="숨긴 그룹"
                                show={true}
                                position={{ x: showHiddenGroup.x, y: showHiddenGroup.y }}
                                minWidth={"180px"}
                                items={spaces
                                    .filter((space) => space.is_hidden)
                                    .map((space) => ({
                                        name: (
                                            <HiddenItemRow>
                                                <HiddenItemAvatar image={space.image}>
                                                    {space.image ? (
                                                        <HiddenItemImg
                                                            src={
                                                                process.env.REACT_APP_ENV === "local"
                                                                    ? `http://${process.env.REACT_APP_API_BASE_URL}${space.image}`
                                                                    : space.image
                                                            }
                                                            alt={space.name}
                                                            status={space.status}
                                                        />
                                                    ) : (
                                                        <HiddenItemFallback status={space.status}>
                                                            {space.name?.trim()?.[0] || "?"}
                                                        </HiddenItemFallback>
                                                    )}
                                                </HiddenItemAvatar>

                                                <HiddenItemName>{space.name}</HiddenItemName>
                                                <HiddenItemUnhide
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        updateSpaceMutation.mutate(
                                                            { space_id: space.id, is_hidden: false },
                                                            {
                                                                onSuccess: () => {
                                                                    setToast("숨김을 해제했습니다.", "info");
                                                                    setShowHiddenGroup({ show: false, x: 0, y: 0 });
                                                                },
                                                                onError: () => {
                                                                    setToast(
                                                                        "해제에 실패했습니다. 잠시후 시도해주세요.",
                                                                        "error",
                                                                    );
                                                                },
                                                            },
                                                        );
                                                    }}
                                                >
                                                    해제
                                                </HiddenItemUnhide>
                                            </HiddenItemRow>
                                        ),
                                    }))}
                                onClose={() => {
                                    setShowHiddenGroup({
                                        show: false,
                                        x: 0,
                                        y: 0,
                                    });
                                }}
                                // triggerClassName={`tree-knob-${g.id}`}
                            />
                        )}
                    </Tooltip>
                </SpaceItem>
            </SpaceBarConatiner>
            {showSubscribeModal && (
                <AbstractModal
                    modalTitle="요금제 결제"
                    width={500}
                    height={640}
                    exitModal={(e) => setShowSubscribeModal(false)}
                >
                    <PaymentSubscribeModal
                        isGroupPlan={true}
                        user={userQuery.data}
                        exitModal={(e) => setShowSubscribeModal(false)}
                    />
                </AbstractModal>
            )}
        </div>
    );
}

export default SpaceBar;
